import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { Divider } from 'antd';
import { Form } from 'ALIASED-antd';
import { AnimatePresence } from 'motion/react';
import Grid from '@components/UI/Grid';
import useUser from '@vl-core/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@vl-core/useRuntimeConfig';
import { Case } from 'vl-common/src/schemas/shared';
import styled from '@emotion/styled';
import DocmanODSFields from '../Docman/DocmanODSFields';
import { RegistrationFormItem } from '../Registration/RegistrationFormItem';
import { useRegistrationFormInstance } from '../Registration/useSelfRegistrationFormInstance';
import { ConsentToShareWithGPFormItems } from '../Registration/items/ConsentToShareWithGPFormItems';
import { formatPracticeFromObject } from '@helpers/formatAddress';

const GroupSection = styled.div`
  border-radius: 0.5rem;
  background-color: #f9fafb;
  padding: 1rem;
`;

export const DocmanConsentQuestions = ({ caseDetails }: { caseDetails: Case }) => {
  const dispatch = useDispatch();
  const form = useRegistrationFormInstance();
  const user = useUser();
  const { t } = useTranslation();
  const isFirstRender = useRef(true);
  const { DOCMAN } = useRuntimeConfig();

  const { data: gpDetails } = useQuery({
    enabled: !form.getFieldValue('gp_ods_code'),
    queryKey: ['getGPDetails', user.user_guid],
    queryFn: () => dispatch(actions.getGpDetails(user.user_guid))
  });

  const setGpType = useCallback(
    (value: boolean) => {
      return form.setFieldsValue({
        gp_virtual: value,
        gp_postcode: gpDetails?.postcode,
        gp_ods_code: gpDetails?.gp_ods_code,
        docman_active: gpDetails?.docman_active,
        gp_address_preview: gpDetails?.gp_ods_code ? formatPracticeFromObject(gpDetails) : ''
      });
    },
    [form, gpDetails]
  );

  const gpSummaryConsent = Form.useWatch('gp_summary_consent', { form, preserve: true });
  const isGpOdsCodeSelected = Form.useWatch('gp_address_preview', { form, preserve: true });
  const docmanActive = Form.useWatch('docman_active', { form, preserve: true });
  const shouldShowConsent = isGpOdsCodeSelected && DOCMAN && docmanActive;

  useEffect(() => {
    if (isFirstRender.current) {
      setGpType(false);
      isFirstRender.current = false;
      return;
    }
    if (gpDetails?.gp_ods_code) setGpType(false);
    // @ts-expect-error: Existing code implies that this property exists, but I
    // haven't seen it.
    if (user.gp_virtual) setGpType(true);
  }, [gpDetails?.gp_ods_code, user, setGpType]);

  const renderConsentTitle = () => {
    if (gpDetails?.gp_ods_code) {
      return <h2 style={{ padding: 0, margin: 0 }}>{t('Your GP supports electronic sharing')}</h2>;
    }

    return <h2 style={{ padding: 0, margin: 0 }}>{t('Consent to sharing clinical information')}</h2>;
  };

  return (
    <AnimatePresence>
      <Grid columns={1}>
        <fieldset aria-labelledby="gpPractice">
          <div id="gpPractice">
            <h2 style={{ padding: 0, margin: 0 }}>{t('Confirm your GP practice')}</h2>
          </div>
          <Divider style={{ marginTop: 0, marginBottom: '1em' }} aria-hidden="true" />
          <GroupSection>
            <DocmanODSFields
              form={form}
              FormItemComponent={RegistrationFormItem}
              searchFieldName="gp_postcode"
              previewFieldName="gp_address_preview"
              odsCodeFieldName="gp_ods_code"
              // Do not set initial search value otherwise patient flow is confusing
              // initialSearchValue={form.getFieldValue('postcode') || user.postcode}
              isConsentGiven={gpSummaryConsent}
              hasSavedGPWithDocmanNotActive={!!isGpOdsCodeSelected && !docmanActive}
            />
          </GroupSection>
        </fieldset>
        {shouldShowConsent && (
          <fieldset aria-labelledby="consentSharing">
            <div id="consentSharing">{renderConsentTitle()}</div>
            <Divider style={{ padding: 0, margin: '0 0 1rem' }} aria-hidden="true" />
            <GroupSection>
              <ConsentToShareWithGPFormItems
                FormItemComponent={RegistrationFormItem}
                form={form}
                summaryLettersFieldName="gp_summary_consent"
                isConsentAllowed={gpSummaryConsent}
                nhsNumberFieldName="nhs_number"
                caseDetails={caseDetails}
              />
            </GroupSection>
          </fieldset>
        )}
      </Grid>
    </AnimatePresence>
  );
};
